<template>
  <div>
    <ts-dialog title="Verify your assets before upload" :visible.sync="visible" width="850px" center>
      <div class="list-actions">
        <div class="select">
          <ts-link @click="handleSelectAllDroppedAssetsClick">Select all</ts-link>
          <ts-link @click="handleUnselectAllDroppedAssetsClick">Unselect all</ts-link>
        </div>
        <div class="actions">
          <div class="platforms">
            <MultiSelectionDropdown
              placeholder="Select platform(s)"
              size="small"
              :width="220"
              style="margin-right: 10px"
              :options="platforms"
              @input="handleSelectedDroppedAssetsPlatforms"
            ></MultiSelectionDropdown>
          </div>
          <ts-button size="small" filled @click="handleDeleteDroppedAssetsClick(selectedDroppedAssets)">
            Delete
          </ts-button>
        </div>
      </div>
      <div class="droped-assets-container">
        <div v-for="(v, index) in $v.droppedAssets.$each.$iter" :key="index" class="row form-row">
          <ts-checkbox
            class="checkbox"
            :value="isDroppedAssetSelected(v.$model)"
            @input="handleDroppedAssetSelectedChange(v.$model, $event)"
          ></ts-checkbox>
          <ts-form-item label="File name" :validation="v.name" class="form-item">
            <ts-input v-model.lazy="v.name.$model"></ts-input>
          </ts-form-item>
          <ts-form-item label="Platform(s)" :validation="v.platforms" class="form-item">
            <MultiSelectionDropdown
              placeholder="Select platform(s)"
              size="large"
              :width="320"
              :options="platforms"
              v-model.lazy="v.platforms.$model"
            ></MultiSelectionDropdown>
          </ts-form-item>
          <div class="actions">
            <ts-button circle @click="handleDeleteDroppedAssetsClick([v.$model])">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </div>
        </div>
      </div>
      <span slot="footer">
        <ts-button @click="handleUploadClick">Upload</ts-button>
      </span>
    </ts-dialog>
    <ts-dialog :visible.sync="showMissingAssetWarningDialog" width="650px">
      <div class="w-full mx-auto text-center mb-4 break-word">
        <h1 class="border-b border-gray-600 text-center py-2 mb-6 font-normal">
          SOMETHING IS MISSING!
        </h1>
        <p class="text-base text-center">
          Now that final assets are ready for your clients, are some of them worth uploading to the Source ?
          If so, please make sure to drag & drop key masters of your project as individual files,
          so that you can publish the assets to The Source.
          <br>Thank You!
        </p>
      </div>
      <template #footer>
        <div class="flex items-center">
          <ts-button @click="showMissingAssetWarningDialog = false" filled type="primary" size="medium">OK let’s do it</ts-button>
        </div>
      </template>
    </ts-dialog>
    <AlertInfoBox message="Please select the assets you want to share."/>
    <div v-if="assetsAndUploadingAssets.length > 0" class="list-actions">
      <div class="select" style="flex: none;">
        <ts-link @click="handleSelectAllAssetsClick">Select all</ts-link>
        <ts-link @click="handleUnselectAllAssetsClick">Unselect all</ts-link>
      </div>
      <div class="actions" v-if="hasSelectedAssets">
        <change-final-assets-platform :assets="selectedUploadedAssets" />
        <share-final-assets :assets="selectedUploadedAssets" v-if="isAdmin || !isCreator"></share-final-assets>
        <publish-to-the-source :assets="selectedUploadedAssets" />
        <!-- <visible :when="false"> -->
        <PublishFinalAsset v-if="isProjectManager || isCustomer" :assets="selectedUploadedAssets" />
        <!-- </visible> -->
        <grouped-actions @input="handleGroupedActionsEvent" />
        <!-- <ts-button size="small" @click="handleDeleteAssetsClick(selectedAssets)"> Delete </ts-button>
        <ts-button :pending="!canDownloadAssets" size="small" @click="handleDownloadAssetsClick(selectedAssets)">
          Download
        </ts-button> -->
      </div>
    </div>
    <div ref="droptarget" class="droptarget" @dragover="isDragging = true" @dragleave="isDragging = false">
      <div class="list">
        <div
          v-for="asset in assetsAndUploadingAssets"
          :key="asset.id"
          class="row"
          :class="{ 'upload-error': asset.uploadError }"
        >
          <ts-checkbox
            class="checkbox"
            :value="isAssetSelected(asset)"
            @input="handleAssetSelectedChange(asset, $event)"
          ></ts-checkbox>
          <div class="thumbnail">
            <Thumbnail v-if="asset.file && asset.file.thumbnail" :url="asset.file.thumbnail" />
          </div>
          <div class="file-info" :class="{ uploading: isAssetUploading(asset) }">
            <p class="file-name">{{ asset.name }}{{ asset.error }}</p>
            <div v-if="asset.uploadError" class="upload-error-message">
              {{ asset.uploadErrorMessage }}
            </div>
            <div v-else-if="isAssetUploading(asset)">
              <ts-progress :percentage="asset.resumableFile.tsUploadProgress * 100"></ts-progress>
            </div>
            <p v-else class="file-creation-info">
              Created on {{ formatDate(asset.createdAt) }} by {{ asset.createdBy.name }}
            </p>
          </div>
          <div v-if="!isAssetUploading(asset)" class="platforms">
            <PlatformList :platforms="asset.platforms" />
          </div>
          <div class="publication">
            <template v-if="asset.publications && asset.publications.length > 0">
              <div class="" v-for="publication in asset.publications" :key="publication.id">
              <p class="publication_title">Published on {{publication.target}}</p>
              <p class="publication_subtitle">{{publication.description}}</p>
              </div>
            </template>
            <template v-else>
              <p class="publication_title">Unpublished</p>
            </template>
          </div>
          <div class="actions">
            <div v-if="!isAssetUploading(asset)" class="actions">
              <ts-button
                :pending="isPending(asset.status)"
                size="small"
                circle
                @click="handleDownloadAssetClick(asset)"
              >
                <ts-icon
                  icon="save"
                  size="x-small"
                  color="#6d7587"
                  :pending="isPending(asset.status)"
                  :title="isPending(asset.status) ? 'Pending' : 'Download'"
                />
              </ts-button>
              <FilePreview
                v-if="isFilePreviewable(asset.file)"
                :file="asset.file"
                :show-edit="false"
                @delete="handleDeleteAssetsClick([asset])"
              />
              <EditFinalAsset :platforms="platforms" :value="asset" @input="handleAssetEdit" />
            </div>
            <ts-button size="small" circle @click="handleDeleteAssetsClick([asset])">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </div>
        </div>
      </div>
      <div v-if="isProjectManager || isCreator" ref="droptargetPlaceholder" class="placeholder">
        <DropTarget label="" :active="isDragging" :show-dropin="assetsAndUploadingAssets.length === 0">
          <template #title v-if="isProjectManager || isCreator">
            <p class="font-bold text-center">Final assets</p>
            Have your assets been validated by the client? <br />
            Make sure final exports are compliant with technical specs and <br />
            drop them here for delivery !
          </template>
        </DropTarget>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flex{
  display: flex;
}

.items-center{
  justify-content: center;
}
.text-base {
  font-size: 1rem;
}
.mb-6 {
  margin-bottom: 1.5rem;
}
.text-center {
  text-align: center;
}
.max-w-lg {
  max-width: 32rem;
}
.break-word{
  word-break: break-word;
}
.w-full{
  width: 100%;
}
.py-2{
  padding-top: .5rem;
  padding-bottom: .5rem;
}
.border-b {
  border-bottom-style: solid;
  border-bottom-width: 2px;
}
.border-gray-600 {
  border-color: #dbe2ea;
}
.font-normal {
  font-weight: normal;
}
.publication{
  flex-grow: 1;
  .publication_title{
  font-family: Roboto;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.17;
  letter-spacing: normal;
  color: #6d7587;
  }
  .publication_subtitle{
    font-family: Roboto;
  font-size: 10px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
color: #acbacc;
  }
}
.droped-assets-container {
  max-height: 400px;
  overflow-y: auto;
}
.grouped-actions:focus {
  outline: 0;
}
.grouped-actions {
  padding: 6px 19px;
  font-size: 12px !important;
  border-radius: 24px;
  box-shadow: 5px 5px 10px 0 rgba(166, 171, 189, 0.48), -5px -5px 10px 0 rgba(250, 251, 255, 0.8);
  border: solid 2px rgba(255, 255, 255, 0.16);
  background-blend-mode: soft-light, normal;
  background-image: linear-gradient(278deg, #000000, var(--fills-neutral-white) 0%),
    linear-gradient(to bottom, #ebecf0, #ebecf0);
}
.actions {
  display: flex;
}
.hidden {
  display: none !important;
}
.font-bold {
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
}
.list-actions {
  margin-bottom: 20px;
  display: flex;

  .select {
    flex-grow: 1;

    > * {
      margin-right: 23px;
    }
  }

  .actions {
    display: flex;

    > * {
      margin-left: 8px;
    }
  }
}

.droptarget {
  .placeholder {
    display: flex;
    justify-content: center;
    margin: 50px;
  }
}

.row {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.16);
  box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
  border-radius: 16px;
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  padding: 12px 18px;

  &.upload-error {
    border-color: #f56c6c;
  }

  .checkbox {
    margin-right: 13px;
  }

  .thumbnail {
    width: 48px;
    margin-right: 12px;
  }

  .file-info {
    width: 250px;
    padding-right: 12px;

    &.uploading {
      width: auto;
      flex-grow: 1;
    }
  }

  .file-name {
    font-family: Roboto;
    font-size: 12px;
    color: #6d7587;
    overflow-wrap: break-word;
  }

  .file-creation-info {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #acbacc;
  }

  .upload-error-message {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #f56c6c;
  }

  .platforms {
    flex-grow: 1;
  }

  .actions {
    display: flex;
    align-items: center;

    > * {
      margin-left: 10px;
    }
  }
}

.form-row {
  align-items: flex-end;

  .checkbox {
    margin-bottom: 30px;
  }

  .actions {
    margin-bottom: 14px;
  }

  .form-item {
    margin: 0 10px;
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import { downloadProjectFinalAssets } from '@/api/workflow';

import EditFinalAsset from './EditFinalAsset.vue';
import PublishFinalAsset from './PublishFinalAsset.vue';
import PlatformList from '@/components/PlatformList.vue';
import Thumbnail from '@/components/Thumbnail.vue';
import MultiSelectionDropdown from '@/components/inputs/MultiSelectionDropdown.vue';
import DropTarget from '@/components/DropTarget.vue';
import FilePreview from '@/components/FilePreview.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';
import ShareFinalAssets from './ShareFinalAssets.vue';
import PublishToTheSource from './PublishToTheSource.vue';
import GroupedActions from '@/components/inputs/GroupedActions.vue';
import ChangeFinalAssetsPlatform from './ChangeFinalAssetsPlatform.vue';
import AlertInfoBox from "@/components/widgets/AlertInfoBox.vue";

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    EditFinalAsset,
    PublishFinalAsset,
    PlatformList,
    AlertInfoBox,
    Thumbnail,
    MultiSelectionDropdown,
    DropTarget,
    FilePreview,
    ShareFinalAssets,
    PublishToTheSource,
    GroupedActions,
    ChangeFinalAssetsPlatform
  },
  props: {
    assets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedAssets: [],
      isDragging: false,
      showMissingAssetWarningDialog: false,
      droppedAssets: [],
      droppedAssetsPlatforms: [],
      selectedDroppedAssets: [],
      uploadingAssets: [],
      visible: false
    };
  },
  validations: {
    droppedAssets: {
      $each: {
        name: {
          required
        },
        platforms: {
          required
        }
      }
    }
  },
  computed: {
    ...mapState('workflow', {
      platforms: state => state.myProjects.options.platforms,
      currentProjectId: state => state.currentProjectId
    }),
    hasSelectedAssets() {
      return this.selectedAssets.length > 0;
    },
    canDownloadAssets() {
      return (
        this.selectedAssets.length > 0 &&
        this.selectedAssets.every(asset => {
          return asset.file?.status === 'processed';
        })
      );
    },
    assetsAndUploadingAssets() {
      return [...this.assets, ...this.uploadingAssets];
    },
    selectedUploadedAssets() {
      const selectedAssetIds = this.selectedAssets.filter(asset => !asset.resumableFile).map(b => b.id);

      return this.assets.filter(b => selectedAssetIds.includes(b.id));
    }
  },
  methods: {
    ...mapActions('workflow', ['fetchProjectOptions']),
    ...mapActions('workflow/delivery', [
      'addCurrentProjectFinalAsset',
      'updateCurrentProjectFinalAsset',
      'removeCurrentProjectFinalAsset'
    ]),
    handleSelectedDroppedAssetsPlatforms(platforms) {
      const updatePlatforms = asset => {
        asset.platforms = platforms;
        return asset;
      };

      if (this.selectedDroppedAssets.length == 0) {
        this.droppedAssets = this.droppedAssets.map(updatePlatforms);
        return;
      }

      this.selectedDroppedAssets = this.selectedDroppedAssets.map(updatePlatforms);
    },
    handleGroupedActionsEvent(input) {
      this[input](this.selectedAssets);
    },
    isAssetSelected(asset) {
      return !!this.selectedAssets.find(a => a.id === asset.id);
    },
    isAssetUploading(asset) {
      return asset.resumableFile && !asset.resumableFile.tsIsComplete;
    },
    handleSelectAllAssetsClick() {
      this.selectedAssets = this.assets;
    },
    handleUnselectAllAssetsClick() {
      this.selectedAssets = [];
    },
    handleAssetSelectedChange(asset, value) {
      if (value) {
        this.selectedAssets = [...this.selectedAssets, asset];
      } else {
        this.selectedAssets = this.selectedAssets.filter(a => a.id !== asset.id);
      }
    },
    handleSelectPlatformClick() {
      window.EventBus.$emit('Delivery.PlatformSelection');
    },
    handleDownloadAssetClick(asset) {
      if (this.isPending(asset.status)) {
        window.EventBus.$emit('download.pending');
        return;
      }
      window.open(asset.file.url, '_blank');
    },
    handleAssetEdit(asset) {
      this.updateCurrentProjectFinalAsset({ asset });
    },
    handleDeleteAssetsClick(assets) {
      if (!confirm('Delete these assets?')) {
        return;
      }

      assets
        .filter(asset => !!asset.resumableFile)
        .forEach(asset => {
          this.resumable.removeFile(asset.resumableFile);
          this.uploadingAssets = this.uploadingAssets.filter(
            a => a.resumableFile.uniqueIdentifier !== asset.resumableFile.uniqueIdentifier
          );
          this.$emit('delete-asset', asset);
        });
      assets
        .filter(asset => !asset.resumableFile)
        .forEach(asset => {
          this.removeCurrentProjectFinalAsset({ asset });
        });
    },
    handleDownloadAssetsClick(assets) {
      if (!this.canDownloadAssets) {
        window.EventBus.$emit('download.pending');
        return;
      }
      const uploadedAssets = assets.filter(asset => !asset.resumableFile);
      if (uploadedAssets.length > 1) {
        this.showDownloadProcessing();
        this.downloadFile(
          downloadProjectFinalAssets({
            projectId: this.currentProjectId,
            assetIds: uploadedAssets.map(a => a.id)
          }),
          `ts_final_assets_project${this.currentProjectId}.zip`
        );
        return;
      }

      uploadedAssets.length === 1 && this.handleDownloadAssetClick(uploadedAssets[0]);
    },
    isDroppedAssetSelected(asset) {
      return !!this.selectedDroppedAssets.find(
        a => a.resumableFile.uniqueIdentifier === asset.resumableFile.uniqueIdentifier
      );
    },
    handleDroppedAssetSelectedChange(asset, value) {
      if (value) {
        this.selectedDroppedAssets = [...this.selectedDroppedAssets, asset];
      } else {
        this.selectedDroppedAssets = this.selectedDroppedAssets.filter(
          a => a.resumableFile.uniqueIdentifier !== asset.resumableFile.uniqueIdentifier
        );
      }
    },
    handleSelectAllDroppedAssetsClick() {
      this.selectedDroppedAssets = this.droppedAssets;
    },
    handleUnselectAllDroppedAssetsClick() {
      this.selectedDroppedAssets = [];
    },
    handleDeleteDroppedAssetsClick(assets) {
      assets.forEach(asset => {
        this.resumable.removeFile(asset.resumableFile);
        this.droppedAssets = this.droppedAssets.filter(
          a => a.resumableFile.uniqueIdentifier !== asset.resumableFile.uniqueIdentifier
        );
      });
    },
    // resetUploadForm() {
    // this.resumable.cancel();
    // this.droppedAssets = [];
    // },
    uploading(id, uploading) {
      this.uploadingAssets = this.uploadingAssets.map(u => {
        if (u.resumableFile.uniqueIdentifier === id) {
          return u;
        }

        u.uploading = uploading;

        return u;
      });
    },
    handleUploadClick() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.uploadingAssets = [...this.uploadingAssets, ...this.droppedAssets];
        this.visible = false;
        this.droppedAssets = [];
        this.resumable.upload();
      }
    }
  },
  mounted() {
    this.fetchProjectOptions();
    if (this.isProjectManager || this.isCreator) {
      this.resumable.assignBrowse(this.$refs.droptargetPlaceholder);
      this.resumable.assignDrop(this.$refs.droptarget);
    }
    this.resumable.on('fileAdded', file => {
      file.fileName = this.addSuffix(file.fileName, '' +
        '');
      this.droppedAssets = [
        ...this.droppedAssets,
        {
          id: file.uniqueIdentifier,
          name: file.fileName,
          resumableFile: file,
          platforms: [],
          createdAt: new Date().toISOString(),
          createdBy: this.currentUser,
          uploadError: false,
          uploadErrorMessage: ''
        }
      ];
      this.visible = true;
    });
    this.resumable.on('fileSuccess', async file => {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        try {
          this.uploading(file.uniqueIdentifier, true);
          await this.addCurrentProjectFinalAsset({
            asset: { ...asset },
            lastUpload: this.uploadingAssets.filter(u => !u.uploading).length === 1
          });
          this.uploadingAssets = this.uploadingAssets.filter(
            a => a.resumableFile.uniqueIdentifier !== file.uniqueIdentifier
          );
        } catch (err) {
          console.error('Server error when adding final asset', err);
          asset.uploadError = true;
          asset.uploadErrorMessage = err.message;
          this.showError('Error when adding final asset');
          this.uploading(file.uniqueIdentifier, false);
        }
      }
    });
    this.resumable.on('fileError', async (file, message) => {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        asset.uploadError = true;
        asset.uploadErrorMessage = message;
      }
    });
  },
  watch: {
    assets(value) {
      if (value.length > 0 && value.every(f => this.detectZipFile(f.file?.type))) {
        this.showMissingAssetWarningDialog = true;
      }
    }
  }
};
</script>
